/**
 * React Query hook for fetching story document content (PRD or design)
 * Story 199: View story details with PRD and design
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { SectionType } from '../types/document';

/** Document section from API */
export interface DocumentSection {
  /** Internal section ID for history tracking (Story 232). Optional for optimistic updates. */
  id?: string;
  section_key: string;
  title: string;
  body: string;
  anchor: string | null;
  covers: string[] | null;
  seq: number;
  section_type?: SectionType | null; // Story 250: overview, capability, test_strategy
}

/** Metadata for PRD sections - design capabilities that cover each requirement */
export interface PRDSectionMetadata {
  coveredBy: Array<{ anchor: string; title: string }>;
}

/** Metadata for design sections - tasks that implement each capability */
export interface DesignSectionMetadata {
  taskCount: number;
  tasks: Array<{ seq: number; title: string; status: string }>;
  requiredTests: string[];
}

/** Union type for section metadata */
export type SectionMetadata = PRDSectionMetadata | DesignSectionMetadata;

/** Document response from API */
export interface DocumentResponse {
  doc_type: string;
  summary: string | null;
  sections: DocumentSection[];
  /** Metadata keyed by anchor (PRD anchor or design capability) */
  metadata?: Record<string, SectionMetadata>;
}

/** Query key factory for story documents */
export const storyDocumentKeys = {
  all: ['story-documents'] as const,
  document: (projectSlug: string, storyId: string, docType: string) =>
    [...storyDocumentKeys.all, projectSlug, storyId, docType] as const,
};

/** Fetch document content for a story */
async function fetchStoryDocument(
  projectSlug: string,
  storyId: string,
  docType: string
): Promise<DocumentResponse> {
  const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/documents/${docType}`;
  return api.fetch(endpoint);
}

/**
 * Hook to fetch a story's document content (PRD or design)
 *
 * @param projectSlug - Project identifier (e.g., "boxabirds/claude-backlog")
 * @param storyId - Story external ID
 * @param docType - Document type: 'prd' or 'design'
 */
export function useStoryDocument(
  projectSlug: string,
  storyId: string | null,
  docType: string
) {
  return useQuery({
    queryKey: storyDocumentKeys.document(projectSlug, storyId ?? '', docType),
    queryFn: () => fetchStoryDocument(projectSlug, storyId!, docType),
    enabled: !!projectSlug && !!storyId,
  });
}

/** Anchor item from API */
export interface AnchorItem {
  anchor: string;
  section_key: string;
  title: string;
}

/** Anchors list response from API */
export interface AnchorsResponse {
  anchors: AnchorItem[];
}

/** Query key factory for document anchors */
export const documentAnchorsKeys = {
  all: ['document-anchors'] as const,
  list: (projectSlug: string, storyId: string, docType: string) =>
    [...documentAnchorsKeys.all, projectSlug, storyId, docType] as const,
};

/** Fetch anchors for a document type */
async function fetchDocumentAnchors(
  projectSlug: string,
  storyId: string,
  docType: string
): Promise<AnchorsResponse> {
  const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/documents/${docType}/anchors`;
  return api.fetch(endpoint);
}

/**
 * Hook to fetch all anchors for a document type
 * Used to populate coverage dropdowns
 *
 * @param projectSlug - Project identifier
 * @param storyId - Story external ID
 * @param docType - Document type: 'prd' or 'design'
 */
export function useDocumentAnchors(
  projectSlug: string,
  storyId: string | null,
  docType: string
) {
  return useQuery({
    queryKey: documentAnchorsKeys.list(projectSlug, storyId ?? '', docType),
    queryFn: () => fetchDocumentAnchors(projectSlug, storyId!, docType),
    enabled: !!projectSlug && !!storyId,
  });
}
